/**
 * CFS: fills the filter drawer on demand (blocks/filters.liquid, sections/cfs-filters-drawer.liquid).
 *
 * The drawer used to carry a second full copy of every filter in each page and in every section
 * re-render (filter change, "Show more products", hover prefetch): ~315 KB of HTML on Food Cupboard
 * (PageSpeed, 14 Oct 2026). Now <cfs-drawer-filters> starts empty and loads the lists for the current
 * URL from the Section Rendering API:
 * - the HTML is fetched when the browser is idle after load, if a drawer button is on screen
 *   (the Filter button on phones, "More" in a horizontal filter bar), and put in place the moment a
 *   drawer button is pressed or focused, or the drawer opens, so it is normally there before it shows;
 * - after any change of the query (filters, sort, Hide out of stock, Back/Forward; not ?page=) a
 *   filled drawer is refreshed by morphing, which keeps ticks, open groups, scroll and focus.
 * Horizon's own section morph leaves the filled subtree alone (data-skip-subtree-update).
 */
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { DrawerOpenEvent } from '@theme/theme-drawer';

const SECTION = 'cfs-filters-drawer';
const TRIGGER = '[on\\:click*="#filters-drawer"]';
const QUERY_CHANGE = 'cfs:query-change';

/** The query the drawer content depends on: no page, hash or section_id, sorted. */
function contentKey(href = window.location.href) {
  const url = new URL(href);
  url.hash = '';
  url.searchParams.delete('page');
  url.searchParams.delete('section_id');
  url.searchParams.sort();
  return url.toString();
}

// Horizon and CFS scripts change the URL with pushState/replaceState; tell the drawer when the
// part of the query it depends on changes.
let lastKey = contentKey();
const announce = () => {
  const key = contentKey();
  if (key === lastKey) return;
  lastKey = key;
  document.dispatchEvent(new CustomEvent(QUERY_CHANGE));
};
for (const method of /** @type {const} */ (['pushState', 'replaceState'])) {
  const original = history[method];
  history[method] = function (...args) {
    const result = original.apply(this, /** @type {any} */ (args));
    announce();
    return result;
  };
}
window.addEventListener('popstate', announce);

class CfsDrawerFilters extends HTMLElement {
  /** Content key the drawer currently shows, or null when empty or stale. */
  #shown = /** @type {string | null} */ (null);
  /** Whether lists are in place (possibly stale). */
  #filled = false;
  /** Prefetched or in-flight HTML by content key. */
  #html = /** @type {Map<string, Promise<string>>} */ (new Map());
  /** @type {AbortController | null} */
  #controller = null;
  /** Content key being put in place. */
  #pending = /** @type {string | null} */ (null);

  connectedCallback() {
    document.addEventListener(DrawerOpenEvent.eventName, this.#onOpen);
    document.addEventListener('pointerdown', this.#onIntent, true);
    document.addEventListener('focusin', this.#onIntent, true);
    document.addEventListener(QUERY_CHANGE, this.#onQueryChange);
    if (document.readyState === 'complete') this.#schedulePrefetch();
    else window.addEventListener('load', () => this.#schedulePrefetch(), { once: true });
  }

  disconnectedCallback() {
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onOpen);
    document.removeEventListener('pointerdown', this.#onIntent, true);
    document.removeEventListener('focusin', this.#onIntent, true);
    document.removeEventListener(QUERY_CHANGE, this.#onQueryChange);
    this.#controller?.abort();
  }

  #schedulePrefetch() {
    const run = () => {
      // The horizontal bar's "More" button sits in <overflow-list>'s shadow root
      const roots = [document, ...[...document.querySelectorAll('overflow-list')].map((list) => list.shadowRoot)];
      const visible = roots.some((root) =>
        [...(root?.querySelectorAll(TRIGGER) ?? [])].some((el) => !el.hasAttribute('hidden') && el.getClientRects().length > 0)
      );
      if (visible && !this.#filled) this.#fetch(contentKey()).catch(() => {});
    };
    if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 4000 });
    else setTimeout(run, 2000);
  }

  #onIntent = (/** @type {Event} */ event) => {
    // composedPath: the "More" button's trigger is inside a shadow root
    const hit = event.composedPath().some((node) => node instanceof Element && node.matches(TRIGGER));
    if (hit) this.#ensure();
  };

  #onOpen = (/** @type {Event} */ event) => {
    if (/** @type {Element} */ (event.target).id === 'filters-drawer') this.#ensure();
  };

  #onQueryChange = () => {
    this.#shown = null;
    if (this.#filled) this.#ensure();
  };

  /** Shows the lists for the current URL, loading them if needed. */
  #ensure() {
    const key = contentKey();
    if (this.#shown === key || this.#pending === key) return;
    this.#pending = key;
    this.#fetch(key)
      .then((html) => {
        if (key !== contentKey()) return; // the URL moved on; its own update follows
        this.#apply(html);
        this.#shown = key;
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        if (!this.#filled) this.#showError();
      })
      .finally(() => {
        if (this.#pending === key) this.#pending = null;
      });
  }

  /**
   * @param {string} key
   * @returns {Promise<string>}
   */
  #fetch(key) {
    const known = this.#html.get(key);
    if (known) return known;

    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;
    const url = new URL(key);
    url.searchParams.set('section_id', SECTION);
    const promise = fetch(url, { signal: controller.signal }).then((response) => {
      if (!response.ok) throw new Error(`Filters request failed: ${response.status}`);
      return response.text();
    });
    // Keep one entry: older keys are stale once the query changes
    this.#html.clear();
    this.#html.set(key, promise);
    promise.catch(() => this.#html.get(key) === promise && this.#html.delete(key));
    return promise;
  }

  /** @param {string} html */
  #apply(html) {
    const sectionId = this.dataset.sectionId ?? '';
    const doc = new DOMParser().parseFromString(html.replaceAll('__cfs_sid__', sectionId), 'text/html');
    const root = doc.querySelector('[data-cfs-drawer-filters]');
    if (!root) throw new Error('Filters missing from response');

    /** @param {string} selector */
    const part = (selector) => /** @type {HTMLTemplateElement | null} */ (root.querySelector(selector))?.content;
    const next = document.createElement('div');
    for (const content of [
      part(`template[data-part="remove"][data-label="${this.dataset.label === 'true'}"]`),
      part('template[data-part="filters"]'),
      this.dataset.sorting === 'true'
        ? part(`template[data-part="sort"][data-style="${this.dataset.style === 'horizontal' ? 'horizontal' : 'vertical'}"]`)
        : null,
    ]) {
      if (content) next.append(document.importNode(content, true));
    }

    if (this.#filled) {
      morph(this, next, { ...MORPH_OPTIONS, childrenOnly: true });
    } else {
      this.replaceChildren(...next.childNodes);
      this.#filled = true;
    }
    // scroll-hint only recomputes its fade on scroll or resize
    this.closest('scroll-hint')?.dispatchEvent(new Event('scroll'));
  }

  #showError() {
    const message = document.createElement('p');
    message.className = 'cfs-drawer-filters__status';
    message.setAttribute('role', 'status');
    message.textContent = 'Filters didn’t load. ';
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'cfs-link cfs-drawer-filters__retry';
    retry.textContent = 'Try again';
    retry.addEventListener('click', () => {
      this.replaceChildren(Object.assign(document.createElement('p'), { className: 'cfs-drawer-filters__status', textContent: 'Loading filters…' }));
      this.#ensure();
    });
    message.append(retry);
    this.replaceChildren(message);
  }
}

if (!customElements.get('cfs-drawer-filters')) customElements.define('cfs-drawer-filters', CfsDrawerFilters);
